import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Storage key prefix for all filter persistence.
 */
const FILTER_STORAGE_PREFIX = 'filters_';

/**
 * Sections that support filter persistence.
 * Each section has route patterns that identify it.
 */
export const FilterSections = {
    LEARNING_MANAGEMENT: 'learningManagement',
    REPORTS: 'reports'
};

/**
 * Route patterns for each section.
 */
const SECTION_ROUTE_PATTERNS = {
    [FilterSections.LEARNING_MANAGEMENT]: ['/learningManagement'],
    [FilterSections.REPORTS]: ['/reports']
};

/**
 * Check if current path is within a specific section.
 *
 * @param {object} params - Parameters
 * @param {string} params.pathname - Current URL pathname
 * @param {string} params.section - Section name from FilterSections
 * @returns {boolean} True if within the specified section
 */
const isInSection = ({pathname, section}) => {
    const patterns = SECTION_ROUTE_PATTERNS[section];
    if (!patterns) return false;
    return patterns.some(route => pathname.startsWith(route));
};

/**
 * Check if current path is within ANY section that supports filter persistence.
 * Used to conditionally enable filter persistence only for admin sections.
 *
 * @param {object} params - Parameters
 * @param {string} params.pathname - Current URL pathname
 * @returns {boolean} True if within any section that supports filter persistence
 */
export const isInPersistableSection = ({pathname}) => {
    return Object.values(FilterSections).some(section =>
        isInSection({pathname, section})
    );
};

/**
 * Clears all filter keys for a specific section from sessionStorage.
 *
 * @param {object} params - Parameters
 * @param {string} params.section - Section name from FilterSections
 */
export const clearSectionFilters = ({section}) => {
    const patterns = SECTION_ROUTE_PATTERNS[section];
    if (!patterns) return;

    try {
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            // Clear keys that match this section's route patterns
            if (key && key.startsWith(FILTER_STORAGE_PREFIX)) {
                const storedPath = key.replace(FILTER_STORAGE_PREFIX, '');
                if (patterns.some(pattern => storedPath.startsWith(pattern))) {
                    keysToRemove.push(key);
                }
            }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
    } catch (error) {
        console.error(`Error clearing ${section} filters from sessionStorage:`, error);
    }
};

/**
 * Hook to automatically clear filters when navigating away from a section.
 * Should be used in a component that renders on all routes (e.g., AppSideMenu).
 *
 * Clears filters when:
 * - User navigates 2+ steps away from a section (e.g., LearningManagement -> detail -> somewhere else)
 *
 * Preserves filters when:
 * - User navigates within the same section
 * - User navigates 1 step away (to allow viewing details and returning via back button)
 */
export const useClearFiltersOnRouteChange = () => {
    const location = useLocation();
    const previousPathRef = useRef(location.pathname);

    // Track the "origin section" - the section user was in before leaving
    // Key: section name, Value: true if user was in that section on previous navigation
    const originSectionRef = useRef({});

    useEffect(() => {
        const previousPath = previousPathRef.current;
        const currentPath = location.pathname;

        // Check each section
        Object.values(FilterSections).forEach(section => {
            const wasInSection = isInSection({pathname: previousPath, section});
            const isNowInSection = isInSection({pathname: currentPath, section});
            const hadOriginInSection = originSectionRef.current[section];

            if (isNowInSection) {
                // Returned to the section - clear origin tracking
                originSectionRef.current[section] = false;
            } else if (wasInSection && !isNowInSection) {
                // Just left the section - mark as origin (allow 1 step away)
                originSectionRef.current[section] = true;
            } else if (hadOriginInSection && !isNowInSection) {
                // Was already 1 step away, now navigating further - clear filters
                clearSectionFilters({section});
                originSectionRef.current[section] = false;
            }
        });

        // Update ref for next comparison
        previousPathRef.current = currentPath;
    }, [location.pathname]);
};

/**
 * Generate storage key based on the current route path.
 * Uses the path as the unique identifier for filter persistence.
 *
 * @param {object} params - Parameters
 * @param {string} params.basePath - The base route path (e.g., '/learningManagement/tasks', '/reports/allReports')
 * @returns {string} Unique storage key for sessionStorage
 *
 * @example
 * generateFilterStorageKey({basePath: '/learningManagement/tasks'})
 * // Returns: 'filters_/learningManagement/tasks'
 *
 * @example
 * generateFilterStorageKey({basePath: '/reports/allReports'})
 * // Returns: 'filters_/reports/allReports'
 */
export const generateFilterStorageKey = ({basePath}) => {
    return `${FILTER_STORAGE_PREFIX}${basePath}`;
};

/**
 * Custom hook for persisting filter values in sessionStorage.
 * Follows useCountryCodeCache pattern but uses sessionStorage for tab-session persistence.
 *
 * Filter values are:
 * - Preserved when navigating away and back (e.g., to SignDoc versions page)
 * - Preserved after saving a SignDoc and being redirected
 * - Preserved when using browser back button
 * - Cleared when navigating to a different section (e.g., from Learning Management to Reports)
 * - Cleared on hard refresh (page reload) - this is expected behavior
 *
 * @param {object} params - Hook parameters
 * @param {string} params.storageKey - Unique key for sessionStorage
 * @param {object} params.defaultFilterValues - Default filter values when no stored values exist
 * @returns {object} { filterValues, setFilterValues }
 *
 * @example
 * // Learning Management usage
 * const { filterValues, setFilterValues } = useFilterPersistence({
 *     storageKey: 'learningManagement_filters_FORCE_COMPLETION',
 *     defaultFilterValues: { search: '', types: [] }
 * });
 *
 * @example
 * // Reports usage
 * const { filterValues, setFilterValues } = useFilterPersistence({
 *     storageKey: 'reports_filters_allReports',
 *     defaultFilterValues: { search: '', types: [], organizationIds: ['all'] }
 * });
 */
export const useFilterPersistence = ({storageKey, defaultFilterValues}) => {
    // Lazy initial state - reads from sessionStorage only once on mount
    const [filterValues, setFilterValuesState] = useState(() => {
        try {
            const stored = sessionStorage.getItem(storageKey);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            console.error('Error reading filter state from sessionStorage:', error);
        }
        return defaultFilterValues;
    });

    /**
     * Updates filter values in both React state and sessionStorage.
     * Saves to sessionStorage first, then updates state to ensure persistence.
     *
     * @param {object} newFilterValues - New filter values to persist
     */
    const setFilterValues = useCallback((newFilterValues) => {
        try {
            sessionStorage.setItem(storageKey, JSON.stringify(newFilterValues));
        } catch (error) {
            console.error('Error saving filter state to sessionStorage:', error);
        }
        setFilterValuesState(newFilterValues);
    }, [storageKey]);

    return { filterValues, setFilterValues };
};
